import { useTranslation } from 'react-i18next';
import { Copy, Plus, Trash2 } from 'lucide-react';
import { BUDGET_OPTIONS, describeBudget, type PricingResult } from '@atek/estimate-pricing';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormField } from '@/shared/components/form';
import { MAX_BUDGETS } from '../../lib/editorState';
import { EditorSection } from './EditorSection';
import { NumberField } from './NumberField';
import type { SectionProps } from './types';

/** B1…Bn: label, subtitle (suggested in the deck language), options and budget discount; plus the internal contingency. */
export function BudgetsSection({ draft, dispatch, readOnly, errors, live }: SectionProps & { live: PricingResult | null }) {
  const { t } = useTranslation('crm');
  const { inputs } = draft;
  const managed = live?.counts.managedSensors ?? 0;
  const full = inputs.budgets.length >= MAX_BUDGETS;

  return (
    <EditorSection
      title={t('estimates.budgets.title')}
      actions={!readOnly && (
        <Button type="button" size="sm" variant="outline" disabled={full} onClick={() => dispatch({ type: 'addBudget' })}>
          <Plus className="h-4 w-4 mr-1" /> {t('estimates.budgets.add')}
        </Button>
      )}
    >
      <NumberField
        id="est-contingency" label={t('estimates.budgets.contingency')} hint={t('estimates.budgets.contingencyHint')} className="sm:w-80"
        value={inputs.contingencyPct} max={100} step={0.01} disabled={readOnly} error={errors['inputs.contingencyPct']}
        onChange={(v) => dispatch({ type: 'setInputs', patch: { contingencyPct: v ?? 0 } })}
      />
      <div className="grid gap-4 lg:grid-cols-2">
        {inputs.budgets.map((budget, index) => {
          const path = `inputs.budgets[${index}]`;
          const id = `est-budget-${budget.key}`;
          return (
            <div key={budget.key} data-testid={`budget-${budget.key}`} className="space-y-3 rounded-md border p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-xs text-muted-foreground">{budget.key}</span>
                {!readOnly && (
                  <div className="flex gap-1">
                    <Button
                      type="button" variant="ghost" size="icon" disabled={full}
                      aria-label={t('estimates.budgets.duplicate', { label: budget.label })}
                      onClick={() => dispatch({ type: 'duplicateBudget', index })}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button" variant="ghost" size="icon" disabled={inputs.budgets.length <= 1}
                      aria-label={t('estimates.budgets.remove', { label: budget.label })}
                      onClick={() => dispatch({ type: 'removeBudget', index })}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
              <FormField label={t('estimates.budgets.label')} htmlFor={`${id}-label`} error={errors[`${path}.label`]}>
                <Input
                  id={`${id}-label`} value={budget.label} maxLength={80} disabled={readOnly}
                  onChange={(e) => dispatch({ type: 'setBudget', index, patch: { label: e.target.value } })}
                />
              </FormField>
              <FormField label={t('estimates.budgets.subtitle')} htmlFor={`${id}-subtitle`} hint={t('estimates.budgets.subtitleHint')} error={errors[`${path}.subtitle`]}>
                <Input
                  id={`${id}-subtitle`} value={budget.subtitle ?? ''} maxLength={300} disabled={readOnly}
                  placeholder={describeBudget(budget.options, managed, draft.language)}
                  onChange={(e) => dispatch({ type: 'setBudget', index, patch: { subtitle: e.target.value || null } })}
                />
              </FormField>
              {/* relative: the sr-only legend is absolutely positioned; without a positioned ancestor it is laid out
                  against the page instead of the scrolling content area, and stretches the window. */}
              <fieldset className="relative grid grid-cols-2 gap-2" disabled={readOnly}>
                <legend className="sr-only">{t('estimates.budgets.options.legend', { label: budget.label })}</legend>
                {BUDGET_OPTIONS.map((option) => (
                  <label key={option} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox" className="h-4 w-4" checked={budget.options[option]}
                      onChange={(e) => dispatch({ type: 'setBudgetOption', index, option, value: e.target.checked })}
                    />
                    {t(`estimates.budgets.options.${option}`)}
                  </label>
                ))}
              </fieldset>
              <NumberField
                id={`${id}-discount`} label={t('estimates.budgets.discount')} value={budget.discountPct} max={100} step={0.01}
                disabled={readOnly} error={errors[`${path}.discountPct`]}
                onChange={(v) => dispatch({ type: 'setBudget', index, patch: { discountPct: v ?? 0 } })}
              />
            </div>
          );
        })}
      </div>
    </EditorSection>
  );
}
